import React, { createContext, useContext, useState, useCallback, useEffect } from "react";

export type PracticeMode = "learn" | "practice";

interface PracticeModeContextValue {
  mode: PracticeMode;
  setMode: (mode: PracticeMode) => void;
  toggleMode: () => void;
}

const STORAGE_KEY = "bordona-practice-mode";

const PracticeModeContext = createContext<PracticeModeContextValue>({
  mode: "learn",
  setMode: () => {},
  toggleMode: () => {},
});

export const PracticeModeProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mode, setModeState] = useState<PracticeMode>(() => {
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved === "practice" || saved === "learn") return saved;
    } catch {}
    return "learn";
  });

  const setMode = useCallback((newMode: PracticeMode) => {
    setModeState(newMode);
    try {
      localStorage.setItem(STORAGE_KEY, newMode);
    } catch {}
  }, []);

  const toggleMode = useCallback(() => {
    setModeState((prev) => {
      const next = prev === "learn" ? "practice" : "learn";
      try {
        localStorage.setItem(STORAGE_KEY, next);
      } catch {}
      return next;
    });
  }, []);

  // Sincronizar entre pestañas
  useEffect(() => {
    const handler = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) {
        if (e.newValue === "practice" || e.newValue === "learn") {
          setModeState(e.newValue);
        }
      }
    };
    window.addEventListener("storage", handler);
    return () => window.removeEventListener("storage", handler);
  }, []);

  return (
    <PracticeModeContext.Provider value={{ mode, setMode, toggleMode }}>
      {children}
    </PracticeModeContext.Provider>
  );
};

export function usePracticeMode(): PracticeModeContextValue {
  return useContext(PracticeModeContext);
}
